import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { TrendingUp, ShieldCheck, Sparkles, ShieldX } from "lucide-react";

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [denied, setDenied] = useState(null);

  useEffect(() => {
    const msg = sessionStorage.getItem("login_denied");
    if (msg) { setDenied(msg); sessionStorage.removeItem("login_denied"); }
  }, []);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard");
  }, [user, loading, navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden bg-[#0b1f4d]">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1614853316695-60bb46998d6a?crop=entropy&cs=srgb&fm=jpg&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative z-10 flex items-center gap-2 text-white">
          <div className="h-10 w-10 rounded-lg bg-emerald-500 flex items-center justify-center">
            <TrendingUp className="h-6 w-6 text-white" />
          </div>
          <span className="font-heading text-xl font-bold">Dividend CS</span>
        </div>
        <div className="relative z-10 text-white max-w-md">
          <h1 className="font-heading text-4xl font-bold leading-tight">
            Begeleid elke belegger naar maandelijks dividendinkomen.
          </h1>
          <p className="mt-4 text-blue-100 text-base leading-relaxed">
            Het interne customer-success platform voor jouw CSM's — milestones,
            call notes, documenten en AI-inzichten op één plek.
          </p>
          <div className="mt-8 space-y-3">
            <div className="flex items-center gap-3 text-blue-50">
              <ShieldCheck className="h-5 w-5 text-emerald-400" />
              <span className="text-sm">Volledig overzicht van het Jaartraject</span>
            </div>
            <div className="flex items-center gap-3 text-blue-50">
              <Sparkles className="h-5 w-5 text-emerald-400" />
              <span className="text-sm">AI health scores & follow-up e-mails</span>
            </div>
          </div>
        </div>
        <div className="relative z-10 text-blue-200 text-xs">© Dividend CS — intern gebruik</div>
      </div>

      {/* Right login */}
      <div className="flex flex-col items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-10 w-10 rounded-lg bg-blue-700 flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-white" />
            </div>
            <span className="font-heading text-xl font-bold text-slate-900">Dividend CS</span>
          </div>
          <h2 className="font-heading text-3xl font-bold text-slate-900">Welkom terug</h2>
          <p className="mt-2 text-slate-500">Log in met je Google Workspace account om verder te gaan.</p>

          {denied && (
            <div data-testid="login-denied-banner" className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
              <ShieldX className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{denied}</span>
            </div>
          )}

          <Button
            data-testid="google-login-btn"
            onClick={handleLogin}
            className="mt-8 w-full h-12 bg-blue-700 hover:bg-blue-800 text-white text-base font-medium gap-3"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path fill="#fff" d="M12 11v2.8h3.95c-.16 1-.64 1.85-1.36 2.42v2h2.2c1.29-1.19 2.03-2.94 2.03-5.02 0-.49-.04-.96-.12-1.4H12z" opacity="0"/>
              <path fill="#4285F4" d="M21.35 11.1H12v3.4h5.35c-.23 1.4-1.68 4.1-5.35 4.1-3.22 0-5.85-2.66-5.85-5.95S8.78 6.7 12 6.7c1.83 0 3.06.78 3.76 1.45l2.56-2.47C16.78 4.13 14.6 3.2 12 3.2 6.92 3.2 2.8 7.32 2.8 12.4S6.92 21.6 12 21.6c5.27 0 8.76-3.7 8.76-8.92 0-.6-.06-1.05-.16-1.5z"/>
            </svg>
            Inloggen met Google
          </Button>

          <p className="mt-6 text-xs text-slate-400 text-center">
            Alleen toegankelijk voor geautoriseerde teamleden.
          </p>
        </div>
      </div>
    </div>
  );
}
